import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileText, Download, ShieldCheck, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageHero } from "./Services";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

export default function ProductDetail() {
  const { slug } = useParams();
  const { data: p, isLoading, isError } = useQuery({ queryKey: ["product", slug], queryFn: () => api.get(`/downloads/products/${slug}`).then(r => r.data) });
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const buy = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/downloads/checkout", { productId: p.id, email, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setLoading(false); }
  };

  if (isLoading) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-500">Loading…</div>;
  if (isError || !p) return <div className="max-w-3xl mx-auto px-6 py-24 text-center"><p className="text-slate-600">Product not found.</p><Link to="/digital-downloads" className="text-electric">Browse downloads</Link></div>;

  const input = "w-full h-11 px-3 rounded-lg border border-input bg-white focus:outline-none focus:ring-2 focus:ring-electric text-sm";
  return (
    <div>
      <PageHero label="Digital Download" title={p.title} subtitle={p.category} />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <Link to="/digital-downloads" className="text-sm text-electric flex items-center gap-1.5 mb-6"><ArrowLeft className="h-4 w-4" /> All downloads</Link>
          <div className="aspect-[16/9] tdw-hero-gradient rounded-2xl relative grid place-items-center mb-6">
            <div className="absolute inset-0 tdw-circuit opacity-40 rounded-2xl" />
            <FileText className="relative h-20 w-20 text-tech" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-navy">About this guide</h2>
          <p className="mt-3 text-slate-700 leading-relaxed">{p.description}</p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-600">
            <span className="flex items-center gap-1.5"><FileText className="h-4 w-4 text-electric" /> PDF{p.fileSize ? ` · ${(p.fileSize / 1024).toFixed(0)} KB` : ""}</span>
            <span className="flex items-center gap-1.5"><Download className="h-4 w-4 text-electric" /> Instant secure download</span>
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-electric" /> Paid via Stripe</span>
          </div>
        </div>
        <aside>
          <form onSubmit={buy} data-testid="buy-form" className="rounded-2xl border-2 border-electric/20 p-6 sticky top-20">
            <p className="text-3xl font-heading font-bold text-navy">{money(p.price)}</p>
            <p className="text-xs text-slate-500 mt-1">One-time purchase · instant download</p>
            <label className="block mt-5"><span className="block text-sm font-medium text-navy mb-1.5">Your email</span>
              <input required type="email" className={input} placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} data-testid="buy-email" /></label>
            <p className="text-[11px] text-slate-400 mt-1.5">We'll send your download here and it appears in your portal.</p>
            <Button type="submit" disabled={loading} className="w-full mt-4 bg-electric hover:bg-tech gap-2" data-testid="buy-now-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4" /> Buy Now</>}
            </Button>
            <div className="mt-4 space-y-1.5 text-xs text-slate-500">
              {["Secure Stripe checkout", "Download link valid 30 days", "Available in your customer portal"].map((t, i) => (
                <p key={i} className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-electric" /> {t}</p>
              ))}
            </div>
          </form>
        </aside>
      </section>
    </div>
  );
}
